import { t } from "i18next";
import jsPDF from "jspdf";
import autoTable, { jsPDFDocument, Styles } from "jspdf-autotable";

import { ADMIN_DELIVERIES_LABELS } from "../../constants";
import {
  DeliveriesLabels,
  DriverDeliveriesListItem
} from "../../financial.types";
import {
  getReportBaseStyles,
  getReportGrid,
  REPORT_BORDER_COLOR,
  REPORT_FONT_SIZE,
  REPORT_MARGIN,
  REPORT_PRIMARY_COLOR
} from "../reportLayout";
import { getDeliveriesRows } from "./getDeliveriesRows";

interface RenderTableProps {
  pdf: jsPDF;
  normalFontStyles: Partial<Styles>;
  deliveries: DriverDeliveriesListItem[];
  labels?: DeliveriesLabels;
  /** Repeated at the top of every page the table spills onto. */
  continuationTitle?: string;
  /** Defaults to sitting directly under the previous table, with no gap. */
  startY?: number;
  font: string;
}

export const renderDeliveriesTable = ({
  pdf,
  normalFontStyles,
  deliveries,
  labels = ADMIN_DELIVERIES_LABELS,
  continuationTitle,
  startY,
  font
}: RenderTableProps) => {
  const grid = getReportGrid(pdf);

  let isFirstPageOfTable = true;

  const headerCellStyles: Partial<Styles> = {
    valign: "middle",
    halign: "center",
    fontStyle: "bold",
    fontSize: REPORT_FONT_SIZE
  };

  autoTable(pdf, {
    theme: "striped",
    startY: startY ?? (pdf as jsPDFDocument).lastAutoTable?.finalY ?? 70,
    head: [
      [
        {
          content: t("Order"),
          styles: { ...headerCellStyles, halign: "left" }
        },
        {
          content: t("Delivery Date"),
          styles: headerCellStyles
        },
        {
          content: t("Order Total"),
          styles: headerCellStyles
        },
        {
          content: t("Fees"),
          styles: headerCellStyles
        },
        {
          content: t(labels.toCollect),
          styles: headerCellStyles
        },
        {
          content: t(labels.toPay),
          styles: headerCellStyles
        },
        {
          content: t(labels.paid),
          styles: headerCellStyles
        },
        {
          content: t("Payment Method"),
          styles: headerCellStyles
        },
        {
          content: t("Status"),
          styles: headerCellStyles
        }
      ]
    ],
    body: getDeliveriesRows({
      deliveries,
      normalFontStyles
    }),
    columnStyles: grid.columnStyles,
    // Pages the table spills onto start below the repeated driver name.
    margin: { ...grid.margin, top: REPORT_MARGIN + 20 },
    tableWidth: grid.tableWidth,
    styles: getReportBaseStyles(font),
    rowPageBreak: "avoid",
    headStyles: { cellPadding: 5, fillColor: REPORT_PRIMARY_COLOR },
    tableLineWidth: 0.5,
    tableLineColor: REPORT_BORDER_COLOR,
    didDrawPage: () => {
      if (isFirstPageOfTable) {
        isFirstPageOfTable = false;

        return;
      }

      if (!continuationTitle) return;

      pdf
        .setFontSize(REPORT_FONT_SIZE + 2)
        .setFont(font, "normal", "bold")
        .text(continuationTitle, REPORT_MARGIN, REPORT_MARGIN);
    }
  });
};
